<!--
@component

For user triggered actions.

```svelte
<Button type="submit" variant="success" onclick={onClick} />
```
-->
<script lang="ts">
import type { ClassValue, HTMLButtonAttributes } from 'svelte/elements';

import { Icon, type IconName } from '../icon';
import { preventHandler } from '../prevent-handler';
import { Progress } from '../progress';

interface Props extends HTMLButtonAttributes {
  /** The button is disabled. */
  disabled?: boolean | undefined;
  /** The type of the button. */
  type?: 'button' | 'submit' | 'reset' | undefined;
  /** The communicated action type to the user. */
  variant?:
    | 'primary'
    | 'dark'
    | 'ghost'
    | 'ghost-outline'
    | 'danger'
    | 'outline-danger'
    | 'success'
    | 'outline-success'
    | 'pending'
    | 'disabled-dark'
    | undefined;
  /** The icon shown in the button. */
  icon?: IconName | undefined;
  /** The width of the button. */
  width?: 'full' | 'default' | undefined;
  /** The height of the button. */
  height?: 'fixed' | 'default' | undefined;
  /** The text size of the button. */
  textSize?: 'text-sm' | 'text-xs' | undefined;
  /** Shows progress indicator. Determinate progress is a @TODO */
  progress?: 'indeterminate' | number | undefined;
  /** The native button element, for binding. */
  button?: HTMLButtonElement | undefined;
  /** Extra class names. */
  class?: ClassValue;
  /** The class name of the button progress. */
  progressClass?: ClassValue;
  /** The class name of the button icon. */
  iconClass?: ClassValue;
  /** The class name of the button content. */
  contentClass?: ClassValue;
}

let {
  button = $bindable(),
  disabled = false,
  type = 'button',
  variant = 'primary',
  icon = undefined,
  width = 'default',
  height = 'default',
  textSize = 'text-xs',
  progress = undefined,
  class: extraClasses,
  progressClass,
  iconClass,
  contentClass,
  onclick,
  children,
  ...otherProps
}: Props = $props();

const progressVariant = $derived.by(() => {
  if (disabled) {
    return 'dark';
  }

  return variant === 'danger' || variant === 'dark' ? 'light' : 'dark';
});
</script>

<button
  {...otherProps}
  {type}
  aria-disabled={disabled || undefined}
  class={[
    'inline-flex items-center justify-center gap-1.5 border whitespace-nowrap select-none',
    {
      'flex w-full': width === 'full',
      'inline-flex': width !== 'full',
      'px-3': !icon,
      'pr-3 pl-2': icon,
      'border-disabled-light! bg-disabled-light! text-disabled-dark cursor-not-allowed':
        disabled,
      'bg-disabled-dark! text-disabled-light cursor-not-allowed!':
        variant === 'disabled-dark' && !disabled,
      'border-light bg-light text-default hover:border-medium hover:bg-medium active:bg-gray-2':
        variant === 'primary' && !disabled,
      'border-gray-9 bg-gray-9 text-white hover:border-black hover:bg-black active:bg-black':
        variant === 'dark' && !disabled,
      'text-default hover:bg-ghost-light active:border-ghost-medium active:bg-ghost-medium border-transparent':
        variant === 'ghost' && !disabled,
      'border-light text-default hover:border-medium hover:bg-ghost-light active:border-medium active:bg-gray-2':
        variant === 'ghost-outline' && !disabled,
      'border-danger-dark bg-danger-dark text-white hover:bg-[#aa2a2b] active:bg-[#9e2728]':
        variant === 'danger' && !disabled,
      'border-success-dark bg-success-dark text-white':
        variant === 'success' && !disabled,
      'border-danger-medium bg-danger-light text-danger-dark hover:bg-[#f5dfdc] active:bg-[#f6d7d3]':
        variant === 'outline-danger' && !disabled,
      'border-success-medium bg-success-light text-success-dark hover:bg-[#d9f2e4] active:bg-[#d8f0e2]':
        variant === 'outline-success' && !disabled,
      ' border-[#d3c1f6] bg-[#f5ebff] text-[#7c2fca] hover:border-[#EBD8FF] hover:bg-[#EBD8FF] active:border-[#EBD8FF] active:bg-[#EBD8FF]':
        variant === 'pending',
    },
    height === 'fixed' ? 'h-7.5' : 'py-1.5',
    textSize,
    extraClasses,
  ]}
  bind:this={button}
  onclick={disabled ? preventHandler(true) : onclick}
>
  {#if progress}
    <span
      class={[
        {
          'text-gray-6': variant === 'primary' || variant === 'ghost',
          'text-gray-7': variant === 'ghost-outline',
          'text-gray-4': disabled,
        },
        progressClass,
      ]}
    >
      <Progress variant={progressVariant} />
    </span>
  {:else if icon}
    <span
      class={[
        {
          'text-gray-6': variant === 'primary' || variant === 'ghost',
          'text-gray-7': variant === 'ghost-outline',
          'text-gray-4': disabled,
        },
        iconClass,
      ]}
    >
      <Icon name={icon} />
    </span>
  {/if}

  <span class={['max-w-full', contentClass]}>
    {@render children?.()}
  </span>
</button>
